/**
 * texto: una línea; acabado: varias líneas; mm: medida entera en mm; entero: número entero;
 * lista: valor de una lista del catálogo; switch: Sí/No (siempre tiene valor).
 */
export type TipoCampoEspecificacion = 'texto' | 'acabado' | 'mm' | 'entero' | 'lista' | 'switch';

/** Listas del catálogo que usan los campos tipo 'lista' (claves de las opciones del formulario del equipo). */
export type ListaEspecificacion = 'maniobras' | 'aperturas';

/** Un campo de especificaciones de un equipo (ascensor o escalera). La vista y la validación usan la misma lista. */
export interface ICampoEspecificacion<T> {
    campo: Extract<keyof T, string>;
    /** Nombre completo: se usa en la etiqueta y en los avisos de campos faltantes. */
    etiqueta: string;
    tipo: TipoCampoEspecificacion;
    /** Solo para tipo 'lista': de qué lista salen las opciones. */
    lista?: ListaEspecificacion;
    /** Switch del que depende: el campo solo se habilita y se pide si ese switch está en Sí. */
    dependeDe?: Extract<keyof T, string>;
}

/** Si el campo aplica: los que dependen de un switch solo aplican con el switch en Sí. */
export const campoAplica = <T>({ dependeDe }: ICampoEspecificacion<T>, valores: T) =>
    !dependeDe || (valores as Record<string, ValorEspecificacion>)[dependeDe] === true;

/** Cada sección es una pestaña del formulario del equipo. */
export interface ISeccionEspecificaciones<T> {
    /** Valor de la pestaña. */
    clave: string;
    titulo: string;
    campos: ICampoEspecificacion<T>[];
}

export type ValorEspecificacion = string | number | boolean | null;

/** Valores que vienen del back: los textos en null (equipos anteriores) pasan a vacío. */
export const especificacionesDesdeApi = <T extends object>(
    secciones: ISeccionEspecificaciones<T>[],
    inicial: T,
    api?: Partial<Record<keyof T, ValorEspecificacion>>
): T => {
    const resultado = { ...inicial } as Record<string, ValorEspecificacion>;
    if (!api) return resultado as T;
    for (const { campo, tipo } of secciones.flatMap(seccion => seccion.campos)) {
        const valor = api[campo];
        // Un switch sin valor (equipos anteriores) arranca en No.
        resultado[campo] = tipo === 'switch'
            ? valor === true
            : tipo === 'mm' || tipo === 'entero'
                ? (typeof valor === 'number' ? valor : null)
                : String(valor ?? '');
    }
    return resultado as T;
};

/** Errores por campo del formulario de un equipo: clave del equipo o de sus especificaciones. */
export type ErroresFormulario = Partial<Record<string, string>>;

export const MENSAJE_CAMPO_OBLIGATORIO = 'Campo obligatorio';

/** Valor de la pestaña "Datos del Equipo". */
export const PESTANA_DATOS = 'generales';

/** Campo obligatorio de la pestaña "Datos del Equipo". */
export interface ICampoDato<F> {
    campo: Extract<keyof F, string>;
    etiqueta: string;
    /** El valor 0 es válido (p. ej. costo o contenedores); si no, los números deben ser mayores a 0. */
    admiteCero?: boolean;
}

export interface IValidacionEquipo {
    errores: ErroresFormulario;
    /** Pestaña con el primer campo pendiente; null si no falta nada. */
    primeraPestanaPendiente: string | null;
}

const especificacionCompleta = <T>(definicion: ICampoEspecificacion<T>, valores: T) => {
    const { campo, tipo } = definicion;
    if (tipo === 'switch' || !campoAplica(definicion, valores)) return true;
    const valor = (valores as Record<string, ValorEspecificacion>)[campo];
    return tipo === 'mm' || tipo === 'entero' ? typeof valor === 'number' : typeof valor === 'string' && valor.trim() !== '';
};

/** Campos pendientes por pestaña: la de datos del equipo y una por sección de especificaciones. */
export const faltantesPorPestana = <T>(secciones: ISeccionEspecificaciones<T>[], errores: ErroresFormulario) => {
    const conteo: Record<string, number> = { [PESTANA_DATOS]: 0 };
    for (const seccion of secciones) conteo[seccion.clave] = 0;
    for (const campo of Object.keys(errores)) {
        const seccion = secciones.find(s => s.campos.some(c => c.campo === campo));
        conteo[seccion?.clave ?? PESTANA_DATOS]++;
    }
    return conteo;
};

/** Campos obligatorios sin diligenciar del equipo (datos y especificaciones). */
export const validarEquipo = <F, T>(
    form: Partial<F>,
    camposDatos: ICampoDato<F>[],
    secciones: ISeccionEspecificaciones<T>[],
    especificaciones: T
): IValidacionEquipo => {
    const errores: ErroresFormulario = {};
    for (const { campo, admiteCero } of camposDatos) {
        const valor = form[campo];
        const falta = admiteCero ? valor === undefined || valor === null || valor === '' : !valor;
        if (falta) errores[campo] = MENSAJE_CAMPO_OBLIGATORIO;
    }
    for (const definicion of secciones.flatMap(s => s.campos)) {
        if (!especificacionCompleta(definicion, especificaciones))
            errores[definicion.campo] = MENSAJE_CAMPO_OBLIGATORIO;
    }
    const conteo = faltantesPorPestana(secciones, errores);
    const pestanas = [PESTANA_DATOS, ...secciones.map(s => s.clave)];
    return { errores, primeraPestanaPendiente: pestanas.find(p => conteo[p] > 0) ?? null };
};
